// Evidence panel for a single answer — shows the tools the LLM called,
// their arguments, and a preview of what came back. Users click through
// to see the transparency behind each answer.

import { useState } from 'react'
import type { AskResponse, ToolCall } from '../lib/types'

const GONG_BASE = 'https://us-46017.app.gong.io/call?id='

function ToolCallBlock({ tc }: { tc: ToolCall }) {
  const [expanded, setExpanded] = useState(false)
  const argsJson = JSON.stringify(tc.arguments, null, 2)

  return (
    <div className="border border-marker-border rounded bg-marker-panel2 mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-3 py-2 hover:bg-marker-panel text-sm font-mono flex items-center gap-2"
      >
        <span className={tc.ok ? 'text-marker-good' : 'text-marker-bad'}>
          {tc.ok ? '✓' : '✗'}
        </span>
        <span className="text-marker-accent">{tc.tool}</span>
        <span className="text-marker-muted text-xs ml-auto">{tc.duration_ms}ms</span>
        <span className="text-marker-muted">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && (
        <div className="px-3 py-2 border-t border-marker-border text-xs">
          <div className="mb-2">
            <div className="text-marker-muted mb-1">arguments:</div>
            <pre className="font-mono whitespace-pre-wrap break-all bg-marker-bg p-2 rounded text-marker-text">
              {argsJson}
            </pre>
          </div>
          <div>
            <div className="text-marker-muted mb-1">result preview:</div>
            <pre className="font-mono whitespace-pre-wrap break-all bg-marker-bg p-2 rounded text-marker-text">
              {tc.result_preview}
            </pre>
          </div>
        </div>
      )}
    </div>
  )
}

export function EvidencePanel({ resp }: { resp: AskResponse }) {
  const [showAll, setShowAll] = useState(false)

  return (
    <div className="mt-3 border-t border-marker-border pt-3">
      <button
        onClick={() => setShowAll(!showAll)}
        className="text-xs text-marker-muted hover:text-marker-text flex items-center gap-2"
      >
        <span>{showAll ? '▾' : '▸'}</span>
        <span>Evidence — {resp.tools_called.length} tool call{resp.tools_called.length === 1 ? '' : 's'}</span>
        <span className="ml-2">·</span>
        <span>{resp.model_used}</span>
        <span>·</span>
        <span>${resp.total_cost_usd.toFixed(3)}</span>
        <span>·</span>
        <span>{(resp.total_duration_ms / 1000).toFixed(1)}s</span>
        <span>·</span>
        <span>{resp.input_tokens.toLocaleString()}→{resp.output_tokens.toLocaleString()} tok</span>
      </button>

      {showAll && (
        <div className="mt-3">
          {resp.context_applied && (resp.context_applied.memory_n > 0 || resp.context_applied.workflow_n > 0
            || resp.context_applied.document_id || resp.context_applied.scope) && (
            <div className="mb-3 text-xs text-marker-muted">
              Context in the prompt:{' '}
              {[
                resp.context_applied.scope ? 'account scope' : null,
                resp.context_applied.memory_n > 0
                  ? `${resp.context_applied.memory_n} memor${resp.context_applied.memory_n === 1 ? 'y' : 'ies'}` : null,
                resp.context_applied.workflow_n > 0
                  ? `${resp.context_applied.workflow_n} saved workflow${resp.context_applied.workflow_n === 1 ? '' : 's'}` : null,
                resp.context_applied.document_id
                  ? `attachment ${resp.context_applied.document_title || ''}${resp.context_applied.document_ok === false ? ' (could not be read)' : ''}`
                  : null,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {resp.definitions_applied.length > 0 && (
            <div className="mb-3 text-xs">
              <div className="text-marker-muted mb-1">Definitions consulted:</div>
              <div className="flex flex-wrap gap-1">
                {resp.definitions_applied.map((d, i) => (
                  <span key={i} className="px-2 py-1 bg-marker-panel2 border border-marker-border rounded text-marker-text">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div>
            {resp.tools_called.map((tc, i) => (
              <ToolCallBlock key={i} tc={tc} />
            ))}
          </div>
          <div className="mt-3 text-xs text-marker-muted font-mono">
            audit_id: {resp.audit_id}
          </div>
        </div>
      )}
    </div>
  )
}

// Helper for turning a call_id into a Gong link — used inline in the
// answer markdown if we later post-process the LLM output. For now, the
// LLM outputs raw call_ids as text and users can copy them.
export function gongCallUrl(callId: string): string {
  return `${GONG_BASE}${callId}`
}
